/**
 * NCR Detail Tab (Step 1) for NCR Edit.
 *
 * Stacked bordered cards grouping fields by concept (Customer & Supplier ·
 * Order Information · Reject Details & Action).
 */

import { Button } from '../../components/ui/button';
import { Save, Send } from 'lucide-react';
import { NonConformanceReport, UserProfile, SourceOfRejection, FaultCodeGroup, FaultCodeCategory, Warehouse } from '../../lib/api';
import { NCREditFormData } from './types';
import { CustomerSupplierSection } from './ncr-detail/CustomerSupplierSection';
import { OrderInformationSection } from './ncr-detail/OrderInformationSection';
import { RejectDetailsSection } from './ncr-detail/RejectDetailsSection';
import { DetailFieldErrors } from './ncr-detail/fieldErrors';

interface Step1TabProps {
  ncr: NonConformanceReport;
  formData: NCREditFormData;
  canEdit: boolean;
  /** Submit and "Close - No 8D Required" belong to a draft; a submitted NCR's
   *  details can be edited under Master Edit without offering them. */
  canSubmit?: boolean;
  /** CR-8707: the admin has Master Edit on; saves made from this tab say so. */
  masterEdit?: boolean;
  saving: boolean;
  customerUsers: UserProfile[];
  sourcesOfRejection: SourceOfRejection[];
  faultGroups: FaultCodeGroup[];
  faultCategories: FaultCodeCategory[];
  /** Show the delivery Warehouse field. Caller gates on the schedule-lines
   *  tenant flag (PlatformSettings.dashboard_tabs.scheduled). */
  showWarehouse: boolean;
  /** CR-7347: options for the Warehouse picker. */
  warehouses: Warehouse[];
  /** May tick "Stock confirmed as contained": a draft's customer, or an admin
   *  with Master Edit on (CR-8707). */
  canConfirmContainment?: boolean;
  /** CR-8706: fields the last Save was refused on, highlighted in place. */
  fieldErrors?: DetailFieldErrors;
  onUpdateFormData: (field: string, value: unknown) => void;
  onSave: () => void;
  onSubmitDetail: () => void;
  onCloseNo8D: () => void;
}

export function NCRDetailTab({
  ncr,
  formData,
  canEdit,
  canSubmit = canEdit,
  masterEdit = false,
  saving,
  customerUsers,
  sourcesOfRejection,
  faultGroups,
  faultCategories,
  showWarehouse,
  warehouses,
  canConfirmContainment = false,
  fieldErrors = {},
  onUpdateFormData,
  onSave,
  onSubmitDetail,
  onCloseNo8D,
}: Step1TabProps) {
  return (
    <>
      <div className="space-y-6">
        {/* ===== Section 1 — Customer & Supplier ===== */}
        <CustomerSupplierSection
          ncr={ncr}
          formData={formData}
          canEdit={canEdit}
          fieldErrors={fieldErrors}
          onUpdateFormData={onUpdateFormData}
        />

        {/* ===== Section 2 — Order Information ===== */}
        <OrderInformationSection
          ncr={ncr}
          formData={formData}
          canEdit={canEdit}
          customerUsers={customerUsers}
          showWarehouse={showWarehouse}
          warehouses={warehouses}
          fieldErrors={fieldErrors}
          onUpdateFormData={onUpdateFormData}
        />

        {/* ===== Section 3 — Reject Details & Action ===== */}
        <RejectDetailsSection
          ncr={ncr}
          formData={formData}
          canEdit={canEdit}
          customerUsers={customerUsers}
          sourcesOfRejection={sourcesOfRejection}
          faultGroups={faultGroups}
          faultCategories={faultCategories}
          canConfirmContainment={canConfirmContainment}
          masterEdit={masterEdit}
          fieldErrors={fieldErrors}
          onUpdateFormData={onUpdateFormData}
        />
      </div>

      {/* Submit buttons below the grouped sections. Save shows wherever the
          containment quantities are editable (canConfirmContainment) so they
          persist. Submit / Close stay draft-only (canSubmit). */}
      {(canEdit || canConfirmContainment) && (
        <div className="flex gap-2 mt-4">
          <Button onClick={onSave} disabled={saving} variant="outline">
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          {canSubmit && (
            <>
              <Button onClick={onSubmitDetail} disabled={saving}>
                <Send className="h-4 w-4 mr-2" />
                Submit
              </Button>
              {formData.is_8d_required === false && (
                <Button onClick={onCloseNo8D} disabled={saving} variant="destructive">
                  Close - No 8D Required
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
